import React from 'react';
import cn from 'classnames';

import Bubble from '@lt/components/components/Bubble';
import HintButton from '@lt/components/components/HintButton';

import { NBSP } from 'src/constants/common';
import TouristFieldInput from '../TouristFieldInput';
import withMobile from '../../../routes/Checkout/CheckoutRoutes/PackageCheckout/hocs/withMobile';
import type { FieldType, TouristFieldProps } from '../types';

import styles from './touristField.module.css';

interface State {
  isValid: boolean;
  focused: boolean;
}

class TouristField extends React.PureComponent<
  Readonly<TouristFieldProps>,
  State
> {
  static getDerivedStateFromProps(props, state) {
    const { errors, stateField } = props;
    const errMsg = errors ? errors[stateField] : null;
    if (errMsg) {
      return { isValid: false };
    }
    if (state.isValid === undefined && !props.value) {
      return null;
    }
    return { isValid: true };
  }

  constructor(props) {
    super(props);

    this.state = {
      focused: false,
      isValid: true,
    };

    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  onFocus() {
    this.setState({ focused: true });
  }

  onBlur() {
    this.setState({ focused: false });
  }

  get isValidationShown() {
    const { stateField, value } = this.props;
    const { isValid, focused } = this.state;

    // no show on empty fields, unless there's an error
    if (!stateField || (!value && isValid)) {
      return false;
    }
    return isValid !== undefined && !focused;
  }

  get fieldId() {
    const { idx, stateField } = this.props;
    return `[${idx}].${stateField}`;
  }

  render() {
    const {
      label,
      extraLabel,
      children,
      errors,
      stateField,
      isMobile,
      tooltipText,
      fieldType,
      value = '',
      className,
      disabled = false,
      ...props
    } = this.props;

    const { isValid, focused } = this.state;
    const errMsg =
      Object.values(errors).length !== 0 ? errors[stateField] : null;
    const isFirstErrorMessage = errMsg
      ? errMsg === Object.values(errors)[0]
      : false;

    const params = {
      ...props,
      errMsg,
      focused,
      stateField,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
    };

    const hasTooltip = Boolean(tooltipText);
    const smallFieldTypes: FieldType[] = ['date', 'number'];
    const bigFieldTypes: FieldType[] = ['name', 'patronymic', 'citizenship'];

    return (
      <div
        className={cn(
          styles.container,
          { [styles.disabledContainer]: disabled },
          className,
        )}
      >
        <div className={styles.label}>
          <p className={cn(styles.text, styles.labelText)}>
            {label}
            {hasTooltip && (
              <Bubble text={tooltipText}>
                <HintButton />
              </Bubble>
            )}
          </p>
          <p className={cn(styles.text, styles.labelText)}>
            {extraLabel && `${NBSP}${extraLabel}`}
          </p>
        </div>
        <Bubble
          permanent={isFirstErrorMessage && !isMobile}
          type="warning"
          text={isFirstErrorMessage && !isMobile ? errMsg : ''}
        >
          <div
            className={cn(styles.input, {
              [styles.invalidInput]: this.isValidationShown && !isValid,
              [styles.smallInput]: smallFieldTypes.includes(fieldType),
              [styles.bigInput]: bigFieldTypes.includes(fieldType),
              [styles.mobileBigInput]:
                bigFieldTypes.includes(fieldType) && isMobile,
            })}
            id={this.fieldId}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(isMobile ? {} : { 'data-validation-error': errMsg })}
          >
            {children || <TouristFieldInput value={value} {...params} />}
          </div>
        </Bubble>
        {isMobile && (
          <p className={cn(styles.text, styles.errorText)}>{errMsg}</p>
        )}
      </div>
    );
  }
}

export default withMobile(TouristField);
